import React, { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { IconInfoCircle } from '@tabler/icons-react'

import { isEmpty, isNil } from 'lodash'

import Select from '@/components/forms/Select'
import { Toggle } from '@/components/forms/Checkbox'
import { Card } from '@/components/cards/Card'
import { Button } from '@/components/buttons/Button'
import { toast } from '@/components/toasts/toast.helpers'
import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'
import { CHANNEL_TYPE, CHANNEL_NAME, MESSAGE_CHANNEL_TYPES } from '@/utils/constants'
import { apiService, fetchingAPI } from '@/api'

export const OrganizationToggleGroup = ({
  production,
  leaderboardVisible,
  isBaltoAdmin,
  isBaltoOrg,
  qaEnabled,
  qaCopilotEnabled,
  dataAccessKey,
  remoteDesktopKey,
  handleModalChange,
  rtnEnabled,
  audioExpiryDays,
  postcallMinDurationMinutes,
  postcallMaxDurationMinutes,
  trackedInsightLimit,
  activeChannels = [],
  channelIngestLimits = {},
  organizationId,
}) => {
  const dispatch = useDispatch()
  const [showDataAccessKey, setShowDataAccessKey] = useState(false)
  const [showRemoteDesktopKey, setShowRemoteDesktopKey] = useState(false)
  const [isResettingQuota, setIsResettingQuota] = useState(false)
  const [quotaStatus, setQuotaStatus] = useState(null)

  const fetchQuotaStatus = async () => {
    if (!organizationId || !isBaltoAdmin) {
      return
    }
    try {
      const response = await fetchingAPI(
        `${apiService.audioIngestion}/upload-calls/quota/${organizationId}`,
        'GET',
        dispatch,
        null,
        null,
        { isSilent: true }
      )
      setQuotaStatus(response)
    } catch (err) {
      // Silently fail - quota display is optional
      console.info('Could not fetch quota status:', err)
    }
  }

  useEffect(() => {
    fetchQuotaStatus()
  }, [organizationId, isBaltoAdmin])

  const handleResetUploadQuota = async () => {
    if (!organizationId) {
      return
    }

    // eslint-disable-next-line no-alert
    const confirmed = window.confirm(
      'Are you sure you want to reset the monthly Upload Calls limit for this organization? This will allow them to upload more calls this month.'
    )
    if (!confirmed) {
      return
    }

    setIsResettingQuota(true)
    try {
      const response = await fetchingAPI(
        `${apiService.audioIngestion}/upload-calls/quota/${organizationId}/reset`,
        'POST',
        dispatch
      )
      toast.success(response.message || 'Monthly upload quota reset successfully')
      fetchQuotaStatus()
    } catch (err) {
      console.error('Failed to reset upload quota:', err)
      toast.error('Failed to reset upload quota. Please try again.', { cause: err })
    } finally {
      setIsResettingQuota(false)
    }
  }

  const autoStartToken = {
    remote_desktop_key: remoteDesktopKey,
  }
  const generateDataAccessKey = {
    data_access_key: dataAccessKey,
  }
  const handleRemoteDesktopClick = () => {
    if (!remoteDesktopKey) {
      handleModalChange(autoStartToken, 'Generate Auto-Start Token')
      setShowRemoteDesktopKey(!showRemoteDesktopKey)
    }
    setShowRemoteDesktopKey(!showRemoteDesktopKey)
  }

  const handleDataAccessKeyClick = () => {
    if (!dataAccessKey) {
      handleModalChange(generateDataAccessKey, 'Generate Data Access Key')
      setShowDataAccessKey(!showDataAccessKey)
    }
    setShowDataAccessKey(!showDataAccessKey)
  }
  const productionChange = {
    production: !production,
  }
  const leaderboardVisibilityChange = {
    desktop_leaderboard_visibility: !leaderboardVisible,
  }
  const qaEnabledChange = {
    qa_enabled: !qaEnabled,
  }
  const qaCopilotEnabledChange = {
    qa_copilot_enabled: !qaCopilotEnabled,
  }
  const rtnEnabledChange = {
    call_summarization_enabled: !rtnEnabled,
  }

  const hasChannel = (channelType) => activeChannels.includes(channelType)
  const getChannelChange = (channelType) => {
    const updatedChannels = hasChannel(channelType)
      ? activeChannels.filter((channel) => channel !== channelType)
      : [...activeChannels, channelType]

    return {
      active_channels: updatedChannels,
      // TODO: remove after all changes are done to promote active_channels as the source of truth
      omnichannel_enabled: updatedChannels.length > 0,
    }
  }

  const audioExpiryOptions = [
    { label: '1 day', value: 1 },
    { label: '30 days', value: 30 },
    { label: '60 days', value: 60 },
    { label: '90 days', value: 90 },
    { label: '120 days', value: 120 },
    { label: '150 days', value: 150 },
    { label: '180 days', value: 180 },
    { label: '1 year', value: 365 },
    { label: '2 years', value: 730 },
    { label: '3 years', value: 1095 },
    { label: '4 years', value: 1460 },
    { label: '5 years', value: 1825 },
    { label: '6 years', value: 2190 },
    { label: '7 years', value: 2555 },
    { label: '8 years', value: 2920 },
    { label: '9 years', value: 3285 },
    { label: '10 years', value: 3650 },
  ]

  const postcallMinDurationOptions = [
    { label: '0 seconds', value: 0 },
    { label: '10 seconds', value: 0.16666666666666666 },
    { label: '15 seconds', value: 0.25 },
    { label: '30 seconds', value: 0.5 },
    { label: '45 seconds', value: 0.75 },
    { label: '1 minute', value: 1 },
  ]

  const postcallMaxDurationOptions = [
    { label: '1 hour', value: 60 },
    { label: '1 hour 10 minutes', value: 70 },
    { label: '1 hour 20 minutes', value: 80 },
    { label: '1 hour 30 minutes', value: 90 },
    { label: '2 hours', value: 120 },
    { label: '2 hours 30 minutes', value: 150 },
    { label: '3 hours', value: 180 },
    { label: '4 hours', value: 240 },
    { label: '5 hours', value: 300 },
  ]

  const trackedInsightLimitOptions = [
    { label: '3 insights', value: 3 },
    { label: '5 insights', value: 5 },
    { label: '10 insights', value: 10 },
    { label: '20 insights', value: 20 },
    { label: '30 insights', value: 30 },
  ]

  // 8,333 is a 100,000/year contract spread evenly across 12 months. Caps above
  // 50,000 are rare enough to be typed in, so they get no preset.
  const ingestLimitPresets = [
    { label: 'No limit', value: null },
    { label: '100 messages', value: 100 },
    { label: '1,000 messages', value: 1000 },
    { label: '8,333 messages', value: 8333 },
    { label: '10,000 messages', value: 10000 },
    { label: '50,000 messages', value: 50000 },
  ]

  // fixed locale so the label (and tests asserting it) don't vary by runtime
  const formatIngestLimit = (value) =>
    isNil(value)
      ? 'no limit'
      : `${Number(value).toLocaleString('en-US')} ${Number(value) === 1 ? 'message' : 'messages'}`

  // A saved free-text value has no preset entry, so give it one with a
  // properly pluralized label ("1 message", not "1 messages")
  const ingestLimitOptionsFor = (channelType) => {
    const currentValue = channelIngestLimits[channelType] ?? null
    const isPreset = ingestLimitPresets.some((option) => option.value === currentValue)

    if (isNil(currentValue) || isPreset) {
      return ingestLimitPresets
    }

    return [{ label: formatIngestLimit(currentValue), value: currentValue }, ...ingestLimitPresets]
  }

  // Limits only make sense for channels the org actually has enabled
  const enabledMessageChannels = MESSAGE_CHANNEL_TYPES.filter((channelType) =>
    activeChannels.includes(channelType)
  )

  const isNumericString = (str) => Number.isFinite(Number(str))

  return (
    <>
      <Card>
        <h3>Production</h3>
        <Toggle
          data-testid="checkbox-production"
          aria-label="Production"
          checked={production || false}
          onChange={() => handleModalChange(productionChange, 'Change Production Mode')}
        />
      </Card>
      <Card>
        <h3>Leaderboard Visibility</h3>
        <Toggle
          data-testid="checkbox-leaderboard"
          aria-label="Leaderboard Visibility"
          checked={leaderboardVisible}
          onChange={() =>
            handleModalChange(leaderboardVisibilityChange, 'Change Leaderboard Visibility')
          }
        />
      </Card>
      {isBaltoAdmin && !isBaltoOrg && (
        <>
          <Card>
            <h3>QA Enabled</h3>
            <Toggle
              data-testid="checkbox-qa"
              aria-label="QA Enabled"
              checked={qaEnabled}
              onChange={() => handleModalChange(qaEnabledChange, 'Change QA Enabled')}
            />
          </Card>
          <Card>
            <h3>Quality Enabled</h3>
            <Toggle
              data-testid="checkbox-qa-copilot"
              aria-label="Quality Enabled"
              checked={qaCopilotEnabled}
              onChange={() => handleModalChange(qaCopilotEnabledChange, 'Change Quality Enabled')}
            />
          </Card>
          <Card className="enable-channels-section">
            <div className="channels-header">
              <h3>Enabled Channels</h3>
              <InteractiveTooltip
                content="Controls which interaction channels are available across Balto."
                placement="top"
              >
                <IconInfoCircle size={18} />
              </InteractiveTooltip>
            </div>
            <div className="channels-toggles">
              <InteractiveTooltip
                content="Calls are always enabled for this organization."
                placement="bottom"
              >
                <div className="channel-toggle">
                  <span className="channel-label">Call</span>
                  <Toggle data-testid="checkbox-channel-call" aria-label="Call" checked disabled />
                </div>
              </InteractiveTooltip>
              <div className="channel-toggle">
                <span className="channel-label">Email</span>
                <Toggle
                  data-testid="checkbox-channel-email"
                  aria-label="Email"
                  checked={hasChannel(CHANNEL_TYPE.EMAIL)}
                  onChange={() =>
                    handleModalChange(getChannelChange(CHANNEL_TYPE.EMAIL), 'Change Email Channel')
                  }
                />
              </div>
              <div className="channel-toggle">
                <span className="channel-label">SMS</span>
                <Toggle
                  data-testid="checkbox-channel-sms"
                  aria-label="SMS"
                  checked={hasChannel(CHANNEL_TYPE.SMS)}
                  onChange={() =>
                    handleModalChange(getChannelChange(CHANNEL_TYPE.SMS), 'Change SMS Channel')
                  }
                />
              </div>
              <div className="channel-toggle">
                <span className="channel-label">Chat</span>
                <Toggle
                  data-testid="checkbox-channel-chat"
                  aria-label="Chat"
                  checked={hasChannel(CHANNEL_TYPE.CHAT)}
                  onChange={() =>
                    handleModalChange(getChannelChange(CHANNEL_TYPE.CHAT), 'Change Chat Channel')
                  }
                />
              </div>
            </div>
          </Card>
          <Card>
            <h3>RTN Enabled</h3>
            <Toggle
              data-testid="checkbox-rtn"
              aria-label="RTN Enabled"
              checked={rtnEnabled}
              onChange={() => handleModalChange(rtnEnabledChange, 'Change RTN Enabled')}
            />
          </Card>
        </>
      )}
      {isBaltoAdmin && !isBaltoOrg && (
        <>
          <Card>
            <h3 data-testid="remote-desktop-key-header">Auto-Start Token</h3>
            {showRemoteDesktopKey && remoteDesktopKey != null && (
              <p data-testid="remote-desktop-key-value">{remoteDesktopKey}</p>
            )}
            <Button brand data-testid="remote-desktop-button" onClick={handleRemoteDesktopClick}>
              {!remoteDesktopKey ? 'Generate' : showRemoteDesktopKey ? 'Hide' : 'Show'}
            </Button>
          </Card>

          <Card>
            <h3 data-testid="data-access-key-header">Data Access Key</h3>
            {showDataAccessKey && dataAccessKey != null && (
              <p data-testid="data-access-key-value">{dataAccessKey}</p>
            )}
            <Button brand data-testid="data-access-key-button" onClick={handleDataAccessKeyClick}>
              {!dataAccessKey ? 'Generate' : showDataAccessKey ? 'Hide' : 'Show'}
            </Button>
          </Card>
          <Card>
            <h3 data-testid="audio-expiry-header">Audio File Expiry</h3>
            <Select
              data-testid="audio-expiry-dropdown"
              options={audioExpiryOptions}
              value={audioExpiryDays}
              onChange={(option) =>
                handleModalChange(
                  { audio_expiry_days: option.value },
                  `Change - Set audio expiry to ${option.value} days`
                )
              }
              isClearable={false}
            />
          </Card>
          <Card>
            <h3 data-testid="postcall-min-duration-header">Postcall Min Duration</h3>
            <Select
              data-testid="postcall-min-duration-dropdown"
              allowFreeText
              isNumberField
              units="minutes"
              options={postcallMinDurationOptions}
              value={postcallMinDurationMinutes}
              onChange={(option) => {
                if (!option) return // shield against user deleting input
                handleModalChange(
                  { postcall_min_duration_minutes: option.value },
                  `Change - Set postcall min duration to ${isNumericString(option.label) ? `${option.label} minutes` : option.label}`
                )
              }}
              isClearable={false}
            />
          </Card>
          <Card>
            <h3 data-testid="postcall-max-duration-header">Postcall Max Duration</h3>
            <Select
              data-testid="postcall-max-duration-dropdown"
              allowFreeText
              isNumberField
              units="minutes"
              options={postcallMaxDurationOptions}
              value={postcallMaxDurationMinutes}
              onChange={(option) => {
                if (!option) return // shield against user deleting input
                handleModalChange(
                  { postcall_max_duration_minutes: option.value },
                  `Change - Set postcall max duration to ${isNumericString(option.label) ? `${option.label} minutes` : option.label}`
                )
              }}
              isClearable={false}
            />
          </Card>
          <Card>
            <h3 data-testid="tracked-insight-limit-header">Tracked Insight Limit</h3>
            <Select
              data-testid="tracked-insight-limit-dropdown"
              allowFreeText
              isNumberField
              units="insights"
              options={trackedInsightLimitOptions}
              value={trackedInsightLimit}
              onChange={(option) => {
                // shield against user deleting input
                if (option) {
                  handleModalChange(
                    { tracked_insight_limit: option.value },
                    `Change - Set tracked insight limit to ${option.value}`
                  )
                }
              }}
              isClearable={false}
            />
          </Card>
          {!isEmpty(enabledMessageChannels) && (
            <Card className="enable-channels-section">
              <div className="channels-header">
                <h3 data-testid="ingest-limits-header">Monthly Ingest Limits</h3>
                <InteractiveTooltip
                  content="Maximum number of messages Balto ingests per channel each calendar month (UTC). Messages over the limit are dropped until the month rolls over. No limit ingests everything. Only channels enabled for this organization are shown."
                  placement="top"
                >
                  <IconInfoCircle size={18} />
                </InteractiveTooltip>
              </div>
              <div className="channels-toggles">
                {enabledMessageChannels.map((channelType) => (
                  <div className="channel-toggle" key={channelType}>
                    <span className="channel-label">{CHANNEL_NAME[channelType]}</span>
                    <Select
                      data-testid={`ingest-limit-dropdown-${channelType}`}
                      aria-label={`${CHANNEL_NAME[channelType]} monthly ingest limit`}
                      allowFreeText
                      isNumberField
                      units="messages"
                      options={ingestLimitOptionsFor(channelType)}
                      value={channelIngestLimits[channelType] ?? null}
                      onChange={(option) => {
                        // shield against user deleting input
                        if (option) {
                          // free-text entries arrive as strings — the API expects int|null
                          const limitValue = isNil(option.value) ? null : Number(option.value)
                          if (limitValue !== null && !Number.isFinite(limitValue)) {
                            return
                          }
                          handleModalChange(
                            { channel_ingest_limits: { [channelType]: limitValue } },
                            `Change - Set ${CHANNEL_NAME[channelType]} monthly ingest limit to ${formatIngestLimit(limitValue)}`
                          )
                        }
                      }}
                      isClearable={false}
                    />
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}
      {isBaltoAdmin && (
        <Card>
          <h3 data-testid="reset-upload-quota-header">
            Upload Calls Monthly Limit
            {quotaStatus &&
              `: ${quotaStatus.quota_used.toLocaleString()} / ${quotaStatus.quota_limit.toLocaleString()}`}
          </h3>
          <Button
            brand
            data-testid="reset-upload-quota-button"
            onClick={handleResetUploadQuota}
            disabled={isResettingQuota}
          >
            {isResettingQuota ? 'Resetting...' : 'Reset Monthly Limit'}
          </Button>
        </Card>
      )}
    </>
  )
}
